/**
 * Board output as text for an agent: UTF-8, with each byte that is not part
 * of a valid sequence written as \xNN, where TextDecoder would write U+FFFD
 * and hide what the board sent.
 */
export class SerialText {
    /** The start of a sequence that the next chunk may complete. */
    #held = new Uint8Array(0);

    decode(chunk: Uint8Array, final = false): string {
        const bytes = new Uint8Array(this.#held.length + chunk.length);
        bytes.set(this.#held);
        bytes.set(chunk, this.#held.length);
        this.#held = new Uint8Array(0);
        let text = "";
        let i = 0;
        while (i < bytes.length) {
            const length = sequenceLength(bytes, i);
            if (length === 0) {
                if (!final && isIncompleteTail(bytes, i)) {
                    this.#held = bytes.slice(i);
                    break;
                }
                text += `\\x${bytes[i]!.toString(16).padStart(2, "0")}`;
                i += 1;
                continue;
            }
            text += UTF8.decode(bytes.subarray(i, i + length));
            i += length;
        }
        return text;
    }
}

const UTF8 = new TextDecoder();

/** The length of the valid UTF-8 sequence at `at`, or 0 if there is none. */
function sequenceLength(bytes: Uint8Array, at: number): number {
    const lead = bytes[at]!;
    if (lead < 0x80) return 1;
    const [length, low, high] =
        lead >= 0xc2 && lead <= 0xdf
            ? [2, 0x80, 0xbf]
            : lead === 0xe0
              ? [3, 0xa0, 0xbf]
              : lead === 0xed
                ? [3, 0x80, 0x9f]
                : lead >= 0xe1 && lead <= 0xef
                  ? [3, 0x80, 0xbf]
                  : lead === 0xf0
                    ? [4, 0x90, 0xbf]
                    : lead === 0xf4
                      ? [4, 0x80, 0x8f]
                      : lead >= 0xf1 && lead <= 0xf3
                        ? [4, 0x80, 0xbf]
                        : [0, 0, 0];
    if (length === 0 || at + length > bytes.length) return 0;
    const second = bytes[at + 1]!;
    if (second < low || second > high) return 0;
    for (let k = 2; k < length; k++) {
        const next = bytes[at + k]!;
        if (next < 0x80 || next > 0xbf) return 0;
    }
    return length;
}

/** Whether the bytes from `at` to the end are a valid sequence cut short. */
function isIncompleteTail(bytes: Uint8Array, at: number): boolean {
    const rest = bytes.length - at;
    if (rest >= 4) return false;
    const padded = new Uint8Array(4);
    padded.set(bytes.subarray(at));
    padded.fill(0x80, rest);
    // With 0x80 as the missing bytes the lead byte of a truncated sequence
    // still passes the check, except after E0 and F0, which need more.
    padded[1] = rest > 1 ? padded[1]! : lowestSecond(bytes[at]!);
    const length = sequenceLength(padded, 0);
    return length > rest;
}

function lowestSecond(lead: number): number {
    return lead === 0xe0 ? 0xa0 : lead === 0xf0 ? 0x90 : 0x80;
}
